import './index.css'
import '@/assets/styles/normalize.scss'
import '@/assets/styles/fonts.scss'
import '@/config/i18n'

import * as Sentry from '@sentry/react'
import { LDContext } from 'launchdarkly-js-sdk-common'
import { asyncWithLDProvider } from 'launchdarkly-react-client-sdk'
import React from 'react'
import ReactDOM from 'react-dom/client'

import App from '@/App'
import {
  CLARITY_PROJECT_ID,
  ENVIRONMENT,
  LAUNCH_DARKLY_SDK_CLIENT
} from '@/constants/env'
import { initializeClarityPreLogin } from '@/utils/clarity.utils'
import { getCountryCodeFromCurrency } from '@/utils/country.utils'

const SENTRY_DSN_STAGING = import.meta.env.VITE_SENTRY_DSN

// Initialize Clarity
if (
  typeof window !== 'undefined' &&
  ENVIRONMENT !== 'development' &&
  CLARITY_PROJECT_ID
) {
  if (!window.clarity) {
    const clarityQueue: unknown[][] = []
    window.clarity = function (...args: unknown[]) {
      clarityQueue.push(args)
    }
    window.clarity.q = clarityQueue
  }

  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.clarity.ms/tag/${CLARITY_PROJECT_ID}`

  const firstScript = document.getElementsByTagName('script')[0]
  if (firstScript?.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript)
  } else {
    document.head.appendChild(script)
  }

  script.onload = () => {
    initializeClarityPreLogin()
  }
}

// todo change to different environments (add NODE_ENV when going to prod)
if (
  typeof window !== 'undefined' &&
  !window.location.host.includes('localhost')
) {
  Sentry.init({
    dsn: SENTRY_DSN_STAGING,
    integrations: [
      new Sentry.BrowserTracing({
        tracePropagationTargets: [
          /^https:\/\/yassir-express-revamp-fe-stg-72rftocvia-ew\.a\.run\.app\//
        ]
      }),
      new Sentry.Replay({
        maskAllText: false,
        blockAllMedia: false
      })
    ],
    tracesSampleRate: 0.005,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    denyUrls: ['localhost:3000', 'http://localhost:3000', 'localhost'],
    environment: ENVIRONMENT
  })
}

;(async () => {
  const currencyCode = localStorage.getItem('currencyCode')
  const country = getCountryCodeFromCurrency(currencyCode)

  const context: LDContext = {
    anonymous: true,
    ...(country && { country })
  }

  const LDProvider = await asyncWithLDProvider({
    clientSideID: LAUNCH_DARKLY_SDK_CLIENT ?? '',
    context,
    reactOptions: {
      useCamelCaseFlagKeys: false
    },
    flags: {
      lmd_revamp_store_web_App_new_socket: false,
      lmd_store_web_restaurant_chains_feature: false,
      lmd_store_webapp_FR_Billing: false,
      lmd_store_webapp_registration_page: false,
      lmd_store_webapp_offers_page: false,
      store_web_app_menu_management: false,
      store_web_app_courrier_management: false,
      store_web_app_branches_management: false,
      store_web_app_branches_performance: false,
      store_web_app_busy_mode: false,
      store_web_app_additional_time_slots: false,
      lmd_store_web_offers_marketing: false,
      'lmd-store-enable-restaurant-wallet-web': false,
      'LMD-Store-Web-WalletPayout-Enabled': false,
      'store-auth-v4-use-lmd-core': false
    }
  })

  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <LDProvider>
        <App />
      </LDProvider>
    </React.StrictMode>
  )
})()
